import { memo, useMemo } from 'react';
import {
  SecondaryButton,
  TertiaryButton,
} from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import { FilterOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/FilterOutlined';

import styles from './HorizontalFilterNav.module.scss';
import { useFacetContext } from '../../context/PlpContext';
import { useExperiencesContext } from '../experiences/Experiences';
import { HorizontalFilterNavDropdown } from '../horizontal-filter-nav-dropdown/HorizontalFilterNavDropdown';
import { Facet } from '../../utils/types/types';
import classNames from 'classnames';

interface TopFilter extends Facet {
  appliedCount: number;
  hasAppliedValues: boolean;
  id: string;
}

type HorizontalFilterNavProps = {
  handleIndividualFacetClick?: (dimensionName: string) => void;
  onAllFiltersClick: () => void;
};

export const HorizontalFilterNav = ({
  handleIndividualFacetClick,
  onAllFiltersClick,
}: HorizontalFilterNavProps) => {
  const { facets, applied } = useFacetContext();
  const {
    isHorizontalFilterNavDropdownFlagOn,
    isHorizontalFilterNavSideDrawerFlagOn,
    isSingleColumnLayout,
  } = useExperiencesContext();

  const topFilters: TopFilter[] = useMemo(() => {
    if (!facets || !Array.isArray(facets)) {
      return [];
    }

    const omittedFilterNames = [
      'new in',
      'offers',
      'stock availability',
      'on offer',
    ];

    const dynamicFilters = facets.filter((facet: Facet) => {
      const facetNameLower = facet.name?.toLowerCase() || '';
      return !omittedFilterNames.includes(facetNameLower);
    });

    return dynamicFilters.slice(0, 5).map((facet: Facet) => {
      const appliedValues =
        applied?.filterCombination
          ?.split('|')
          ?.filter((filter: string) =>
            filter.toLowerCase().startsWith(`${facet.name?.toLowerCase()}:`)
          ) || [];

      return {
        appliedCount: appliedValues.length,
        dimensionName: facet.dimensionName,
        filterTypes: facet.filterTypes,
        hasAppliedValues: appliedValues.length > 0,
        id: facet.dimensionName || facet.name,
        label: facet.label,
        name: facet.name,
        tooltip: facet.tooltip,
        values: facet.values || [],
      };
    });
  }, [facets, applied?.filterCombination]);

  if (!isSingleColumnLayout) {
    return null;
  }

  return (
    <>
      <div
        className={classNames(styles.filtersRow, {
          [styles['filtersRow--dropdown']]: isHorizontalFilterNavDropdownFlagOn,
        })}
        data-testid="horizontal-filter-nav"
      >
        {isHorizontalFilterNavSideDrawerFlagOn &&
          topFilters.map((filter: TopFilter) => (
            <TertiaryButton
              key={filter.id}
              aria-label={`Filter by ${filter.name}${filter.hasAppliedValues ? ' (has selections)' : ''}`}
              data-testid={`filter-button-${filter.name.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => handleIndividualFacetClick(filter.name)}
              small
            >
              {filter.name}
            </TertiaryButton>
          ))}
        {isHorizontalFilterNavDropdownFlagOn && (
          <HorizontalFilterNavDropdown topFilters={topFilters} />
        )}
      </div>
      <SecondaryButton
        aria-label="View all filters"
        className={styles.allFiltersButton}
        data-testid="all-filters-button"
        onClick={onAllFiltersClick}
      >
        <span className={styles.filterIcon} aria-hidden="true">
          <FilterOutlined title="Apply filters" size="medium" />
        </span>
        All Filters
      </SecondaryButton>
    </>
  );
};

export default memo(HorizontalFilterNav);
